import { extractTrpcCause, extractTrpcCode } from "@champ-erp/auth-primitives/web";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App.js";
import { refusAccesSession } from "./auth/disponibilite-session.js";
import {
  mutationIgnoreRecuperationSession,
  sessionCouranteProuvee,
} from "./auth/mutation-session-recovery.js";
import {
  invaliderPreuveSessionApresRefus,
  retirerPreparationsMfaAutresIdentites,
  retirerPreuveIdentiteSession,
} from "./auth/preuve-identite-session.js";
import { purgerContexteSessionLocal } from "./auth/purger-contexte-session.js";
import { installerEcouteBasculeSessionTerminee } from "./auth/session-handoff-lock.js";
import { ME_QUERY_KEY } from "./auth/use-me.js";
import { MiseAJourPwa } from "./components/pwa/MiseAJourPwa.js";
import { ConfirmDialogProvider } from "./components/ui/ConfirmDialog.js";
import { ToastProvider } from "./components/ui/Toaster.js";
import { actualiserCacheParcellesApresLecture } from "./offline/actualiser-cache-parcelles.js";
// La couche hors-ligne (Dexie, ~30 Ko gzip) n'est chargée QUE lorsqu'on s'en
// sert : au succès d'une liste parcellaire, ou lors d'une purge de session.
// Elle était importée statiquement ici, donc présente dans le chunk d'entrée de
// tout le monde — y compris pour afficher /login. Les deux appels sont déjà
// dans des callbacks tolérants à l'asynchrone (`void`).
import "./styles.css";

// Un handoff remplace le cookie pour toute l'origine. Les autres onglets
// doivent relire immédiatement identité et permissions, pas conserver une UI
// appartenant à la session précédente.
installerEcouteBasculeSessionTerminee();

/**
 * Détecte le cas « cookie session orphelin » signalé par le middleware CSRF
 * côté API (cf csrf-double-submit.ts). Le serveur efface déjà les cookies
 * en réponse — on complète côté UI en purgant le cache `me` et en forçant
 * une redirection vers /login. L'utilisateur n'a JAMAIS à intervenir
 * manuellement sur ses cookies, même si sa session est dans un état bizarre
 * suite à un changement d'environnement, une révocation côté serveur, etc.
 */
function isCsrfRecoveryError(error: unknown): boolean {
  return extractTrpcCode(error) === "FORBIDDEN" && extractTrpcCause(error) === "CSRF_TOKEN_INVALID";
}

/**
 * Détecte le cas « utilisateur multi-tenant sans MFA activé » remonté par
 * le middleware backend `mfaSetupGuardMiddleware` (cf trpc.ts). Toute route
 * métier renvoie alors FORBIDDEN avec cause `MFA_SETUP_REQUIRED`. Côté UI,
 * on bascule l'utilisateur sur /profile/mfa/setup pour qu'il finisse
 * l'activation au lieu de lui montrer un message d'erreur générique.
 */
function isMfaSetupRequiredError(error: unknown): boolean {
  return extractTrpcCode(error) === "FORBIDDEN" && extractTrpcCause(error) === "MFA_SETUP_REQUIRED";
}

let mfaSetupRedirectInFlight = false;
function triggerMfaSetupRedirect() {
  if (mfaSetupRedirectInFlight) return;
  mfaSetupRedirectInFlight = true;
  if (typeof window !== "undefined" && window.location.pathname !== "/profile/mfa/setup") {
    // `assign` (vs replace) : on garde l'historique propre — l'URL d'origine
    // reste accessible si l'utilisateur veut Back après activation.
    window.location.assign("/profile/mfa/setup?reason=multi_tenant");
  }
}

/**
 * Bascule l'utilisateur vers /login après un échec qui prouve que la session
 * locale est inutilisable (CSRF désynchronisé, session révoquée). On évite
 * de boucler en mémoizant le déclenchement : un double-trigger pousse vers
 * `replace` une seule fois.
 */
let recoveryRedirectInFlight = false;
function triggerSessionRecovery(
  raison: "session_revoquee" | "cookie_orphelin" = "session_revoquee",
  utilisateurIdProuve: string | null = null,
) {
  if (recoveryRedirectInFlight) return;
  recoveryRedirectInFlight = true;
  const me = queryClient.getQueryData(ME_QUERY_KEY) as
    | { utilisateur?: { id?: unknown } }
    | undefined;
  const utilisateurId =
    typeof me?.utilisateur?.id === "string" && me.utilisateur.id.length > 0
      ? me.utilisateur.id
      : utilisateurIdProuve;

  // Une révocation est involontaire : contrairement au logout, on ne peut pas
  // demander à l'utilisateur de synchroniser d'abord. Les outboxes sont donc
  // copiées dans une base de quarantaine séparée AVANT toute purge active.
  // En cas d'échec de quarantaine, seul le cache mémoire est vidé et IndexedDB
  // reste en place : le prochain handoff refusera la bascule (fail-closed).
  void import("./auth/securiser-session-revoquee.js")
    .then(({ securiserSessionRevoquee }) =>
      securiserSessionRevoquee(queryClient, { utilisateurId, raison }),
    )
    .catch(() => purgerContexteSessionLocal(queryClient, async () => undefined))
    .catch(() => undefined)
    .finally(() => {
      // `assign` (vs replace) : on garde l'URL d'origine dans l'historique pour
      // que le user puisse y revenir après reconnexion via le `?next=`.
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/login?next=${next}&recovered=session`);
      }
    });
}

/**
 * Handler global déclenché à chaque erreur de query/mutation. Si on reçoit
 * UNAUTHORIZED depuis une route quelconque (ex : la session a été révoquée
 * pour cause de suspension/exclusion par un admin pendant que l'user navigue),
 * on sécurise les outboxes puis on redirige immédiatement vers /login.
 *
 * Sans ce hook, la session révoquée ne serait détectée qu'au prochain tick
 * du polling `useMe` (30s), pendant lesquelles l'user verrait des erreurs sur
 * la page sans être éjecté.
 *
 * Cas CSRF désynchronisé (FORBIDDEN + cause CSRF_TOKEN_INVALID) : déclenche
 * un flow de récupération automatique (purge caches + redirect /login). Le
 * serveur a déjà effacé les cookies pourris ; côté UI on complète.
 *
 * Cas particulier : une 401 de `useMe` sans identité mise en cache est normale
 * sur une page publique. Avec une ancienne identité hydratée, elle prouve au
 * contraire une révocation et déclenche la même sécurisation.
 */
function handleQueryError(
  error: unknown,
  query: { queryKey: readonly unknown[]; state?: { data?: unknown } },
) {
  if (isCsrfRecoveryError(error)) {
    triggerSessionRecovery("cookie_orphelin", retirerPreuveIdentiteSession(queryClient));
    return;
  }
  // La preuve est retirée synchroniquement : un refus puis une panne ne doit
  // jamais réactiver data A pendant la sécurisation asynchrone des outboxes.
  const refus = invaliderPreuveSessionApresRefus(queryClient, error, query.queryKey);
  if (isMfaSetupRequiredError(error)) {
    triggerMfaSetupRedirect();
    return;
  }
  if (refus?.utilisateurId) {
    triggerSessionRecovery("session_revoquee", refus.utilisateurId);
  }
}

let verificationSessionEnCours = false;
function confirmerSessionApresMutationUnauthorized() {
  if (verificationSessionEnCours) return;
  verificationSessionEnCours = true;
  // Une 401 de mutation peut être métier (ancien mot de passe, code MFA) :
  // seul auth.me sait dire si le cookie de session est réellement révoqué.
  // Son propre handler d'erreur déclenchera la quarantaine si nécessaire.
  void queryClient
    .refetchQueries({ queryKey: ME_QUERY_KEY, exact: true, type: "all" })
    .catch(() => undefined)
    .finally(() => {
      verificationSessionEnCours = false;
    });
}

function handleMutationError(
  error: unknown,
  _variables: unknown,
  _onMutateResult: unknown,
  mutation: { meta?: Record<string, unknown> },
) {
  // Un mauvais mot de passe ou un token public expiré renvoie aussi 401. Ces
  // flows gèrent leur erreur localement et ne doivent jamais purger Dexie.
  if (mutationIgnoreRecuperationSession(mutation)) return;
  if (isCsrfRecoveryError(error)) {
    triggerSessionRecovery("cookie_orphelin", retirerPreuveIdentiteSession(queryClient));
    return;
  }
  if (isMfaSetupRequiredError(error)) {
    triggerMfaSetupRedirect();
    return;
  }
  if (refusAccesSession(error) !== "UNAUTHORIZED") return;
  // Une mutation protégée derrière RequireAuth dispose d'un auth.me courant.
  // Sans cette preuve, on laisse un prochain auth.me établir l'identité.
  // Une identité seulement mémorisée d'une session précédente est
  // volontairement exclue.
  if (!sessionCouranteProuvee(queryClient.getQueryData(ME_QUERY_KEY))) return;
  confirmerSessionApresMutationUnauthorized();
}

/**
 * Point d'écriture UNIQUE du cache parcelles hors-ligne (Dexie).
 * Une année de projection n'est pas un filtre : toute liste complète peut
 * rafraîchir son tenant, identifié par la clé de requête (jamais par `me`).
 * Les listes réellement filtrées et les tenants indéterminés sont refusés.
 */
function handleQuerySuccess(data: unknown, query: { queryKey: readonly unknown[] }) {
  if (query.queryKey.length === 1 && query.queryKey[0] === ME_QUERY_KEY[0]) {
    retirerPreparationsMfaAutresIdentites(queryClient, data);
  }
  void actualiserCacheParcellesApresLecture(query.queryKey, data, (domaineId, parcelles) =>
    import("./offline/index.js").then((offline) =>
      offline.ecrireCacheParcelles(domaineId, parcelles),
    ),
  ).catch(() => undefined); // Un stockage indisponible n'invalide pas la lecture réseau.
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleQueryError, onSuccess: handleQuerySuccess }),
  mutationCache: new MutationCache({ onError: handleMutationError }),
  defaultOptions: {
    queries: {
      // En dev : pas de retry agressif ; en prod : 2 tentatives
      retry: import.meta.env.DEV ? 0 : 2,
      // Pour le mode hors-ligne (terrain), on garde longtemps en cache
      staleTime: 1000 * 60 * 5, // 5 min
      gcTime: 1000 * 60 * 30, // 30 min
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Élément racine #root introuvable dans index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* Providers globaux : Toaster (notifications) + ConfirmDialog (modales
            de confirmation). Tous deux utilisent createPortal donc ne perturbent
            pas la layout des routes. */}
        <ToastProvider>
          <ConfirmDialogProvider>
            <App />
            <MiseAJourPwa />
          </ConfirmDialogProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
